import {
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_REJECTED,
  ORDER_EVENT_STATUS_VOIDED
} from '@klear/constants/order-event-status'
import type { Credit } from '@klear/credit/credit'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import type { FundingDocuments } from '@klear/invoice-offer/funding-documents'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'

export type FundCheckKey =
  | 'accepted'
  | 'invoice'
  | 'purchaseOrder'
  | 'proofOfAcceptance'
  | 'purchasePrice'
  | 'paymentDetails'
  | 'platformFee'
  | 'pricing'
  | 'ehid'
  | 'creditHeadroom'

export type FundCheckOutcome = 'notNeeded' | 'passed' | 'notCompared'

export type FundCheckRemedy = 'applyDefaultPlatformFee' | 'applyDefaultPaymentDetails' | 'editCustomerCredit'

export type FundCheck = {
  key: FundCheckKey
  reason: string | null
  eventId?: number | null
  outcome?: FundCheckOutcome
  remedy?: FundCheckRemedy
}

export type FundReadinessCredit = {
  classification: 'IG' | 'Insured' | null
  rating: string | null
  allianzLimit: string | null
  hasEhid: boolean
}

export const FUND_READINESS_TABS = ['action', 'quote', 'funded', 'closed', 'all'] as const

export type FundReadinessTab = (typeof FUND_READINESS_TABS)[number]

const QUOTE_STATUSES = [ORDER_EVENT_STATUS_DRAFT, ORDER_EVENT_STATUS_REJECTED]
const CLOSED_STATUSES = [ORDER_EVENT_STATUS_COMPLETED, ORDER_EVENT_STATUS_VOIDED]

export const FUND_READINESS_TAB_STATUSES: Record<FundReadinessTab, readonly string[]> = {
  action: [ORDER_EVENT_STATUS_ACCEPTED],
  quote: QUOTE_STATUSES,
  funded: [ORDER_EVENT_STATUS_FUNDED],
  closed: CLOSED_STATUSES,
  all: [...QUOTE_STATUSES, ORDER_EVENT_STATUS_ACCEPTED, ORDER_EVENT_STATUS_FUNDED, ...CLOSED_STATUSES]
}

export const FUND_READINESS_STATUSES = [
  'ready',
  'fundingFailed',
  'blocked',
  'funding',
  'quote',
  'overdue',
  'repaying',
  'funded',
  'completed',
  'voided'
] as const

export type FundReadinessStatus = (typeof FUND_READINESS_STATUSES)[number]

export type FundReadinessDisbursement = {
  paymentId: number
  paymentIdDisplay: string | null
  status: string
  error: string | null
  sentToProvider: boolean
}

export type FundReadinessRecovery = {
  dueDate: string | null
  remaining: string
  inTransit: boolean
  overdue: boolean
  extensionFees: string | null
}

export type FundReadinessRow = {
  offerId: number
  offerIdDisplay: string
  orderId: number
  orderIdDisplay: string | null
  invoiceId: number
  invoiceIdDisplay: string | null
  status: FundReadinessStatus
  traderId: number | null
  traderName: string | null
  customerId: number | null
  customerName: string | null
  purchasePrice: string
  feeTotal: string | null
  dueDate: string | null
  fundingSpv: string | null
  credit: FundReadinessCredit
  acceptedAt: string | null
  fundedAt: string | null
  repaidAt: string | null
  recovery: FundReadinessRecovery | null
  disbursement: FundReadinessDisbursement | null
  advancePaymentNextStep: string | null
  priceIsEstimate: boolean
  blockedFromFunding: boolean
  canStillBeFunded: boolean
  readyToFund: boolean
  checks: FundCheck[]
  fundBlockers: FundCheck[]
}

export type FundReadinessCounts = Record<FundReadinessTab, number>

export type FundReadinessPage = {
  rows: FundReadinessRow[]
  total: number
  counts: FundReadinessCounts
  summary: { ready: number; blocked: number }
}

export type FundCustomer = { name: string | null; allianzEhid: string | null; credit: Credit | undefined }

type FundEvent = { id: number; status: string }

export type FundRating = { rating: string; agency: string }

export type FundPricing = {
  pricedAt: FundRating[]
  pricesAt: (FundRating & { classification: string }) | null
  refusal: string | null
  funder: { name: string; takesClassification: boolean } | null
}

export type FundFacts = FundingDocuments<FundEvent, FundEvent, FundEvent> & {
  hasPlatformFee: boolean
  customer: FundCustomer | null
  pricing: FundPricing | null
}

export function recoveryOf(
  offer: InvoiceOffer,
  collected: { remaining: Money | null; inTransit: boolean } | undefined,
  extensionFees: Money | undefined,
  asOf: KlearDate
): FundReadinessRecovery {
  const remaining = collected?.remaining ?? null
  return {
    dueDate: offer.endDate?.toJSON() ?? null,
    remaining: (remaining ?? offer.settlementAdvanceRecovery).toJSON(),
    inTransit: collected?.inTransit === true,
    overdue: (offer.isPastDue(asOf) && remaining?.isPositive() === true) || extensionFees != null,
    extensionFees: extensionFees?.toJSON() ?? null
  }
}
